import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import barrierService from './barriersService'

//set initial state
const initialState = {
    barriers: [],
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: ''
}

//create new barrier
export const createBarrier = createAsyncThunk(
    'barriers/create',
    async (barrierData, thunkAPI) => {
        try {
            // ✅ CLEAN: Convert empty strings to null for ObjectId fields BEFORE sending to service
            const cleanedBarrierData = {
                ...barrierData,
                // ✅ Clean title and description
                title: barrierData.title?.trim() || "",
                description: barrierData.description?.trim() || "",
                // ✅ CRITICAL: Convert empty strings to null for ObjectId fields
                threat: barrierData.threat && barrierData.threat.trim()
                    ? barrierData.threat.trim()
                    : null,
                consequence: barrierData.consequence && barrierData.consequence.trim()
                    ? barrierData.consequence.trim()
                    : null,
                bowtie: barrierData.bowtie && barrierData.bowtie.trim()
                    ? barrierData.bowtie.trim()
                    : null
            };

            const token = thunkAPI.getState().auth.user.token
            return await barrierService.createBarrier(cleanedBarrierData, token)
        } catch (error) {
            console.error('❌ createBarrier thunk error:', error);

            const message = (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()

            return thunkAPI.rejectWithValue(message)
        }
    })

//get barriers for a bowtie
export const getBarriers = createAsyncThunk(
    'barriers/getAllForBowtie',
    async (bowtieId, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await barrierService.getBarriers(bowtieId, token)
        } catch (error) {
            const message = (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//delete barrier — accepts id string OR { id, threatId?, consequenceId? } for per-flowline removal
export const deleteBarrier = createAsyncThunk(
    'barriers/delete',
    async (payload, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            const args = typeof payload === 'string' ? { id: payload } : payload
            return await barrierService.deleteBarrier(args, token)
        } catch (error) {
            const message = (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

// Move a barrier from one flowline to another (atomic pull-old + push-new)
export const movePlacement = createAsyncThunk(
    'barriers/movePlacement',
    async ({ id, fromThreatId, fromConsequenceId, toThreatId, toConsequenceId, position }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await barrierService.movePlacement(
                { id, fromThreatId, fromConsequenceId, toThreatId, toConsequenceId, position },
                token
            )
        } catch (error) {
            const message = (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

// Add a linked placement of an existing barrier onto a new flowline
export const addPlacement = createAsyncThunk(
    'barriers/addPlacement',
    async ({ id, threatId, consequenceId, position }, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            return await barrierService.addPlacement({ id, threatId, consequenceId, position }, token)
        } catch (error) {
            const message = (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()
            return thunkAPI.rejectWithValue(message)
        }
    }
)

//update barrier
export const updateBarrier = createAsyncThunk(
    'barriers/update',
    async (barrierData, thunkAPI) => {
        try {
            // ✅ FIXED: Only clean fields that are actually provided
            const cleanedBarrierData = { ...barrierData };

            // Only clean/convert fields that were explicitly provided
            if (barrierData.hasOwnProperty('title')) {
                cleanedBarrierData.title = barrierData.title?.trim() || "";
            }

            if (barrierData.hasOwnProperty('description')) {
                cleanedBarrierData.description = barrierData.description?.trim() || "";
            }

            if (barrierData.hasOwnProperty('threat')) {
                cleanedBarrierData.threat = barrierData.threat && barrierData.threat.trim()
                    ? barrierData.threat.trim()
                    : null;
            }

            if (barrierData.hasOwnProperty('consequence')) {
                cleanedBarrierData.consequence = barrierData.consequence && barrierData.consequence.trim()
                    ? barrierData.consequence.trim()
                    : null;
            }

            if (barrierData.hasOwnProperty('bowtie')) {
                cleanedBarrierData.bowtie = barrierData.bowtie && barrierData.bowtie.trim()
                    ? barrierData.bowtie.trim()
                    : null;
            }

            const token = thunkAPI.getState().auth.user.token
            return await barrierService.updateBarrier(cleanedBarrierData, token)
        } catch (error) {
            console.error('❌ updateBarrier thunk error:', error);

            const message = (error.response &&
                error.response.data &&
                error.response.data.message) ||
                error.message ||
                error.toString()

            return thunkAPI.rejectWithValue(message)
        }
    }
)

//Barrier Slice
export const barrierSlice = createSlice({
    name: 'barriers',
    initialState,
    reducers: {
        reset: (state) => initialState,
        // multiuser.md Phase 2c: applied from a socket broadcast, not a thunk.
        remoteBarrierUpserted: (state, action) => {
            const incoming = action.payload
            const existing = state.barriers.find((b) => b._id === incoming._id)
            if (existing) {
                Object.assign(existing, incoming)
            } else {
                state.barriers.push(incoming)
            }
        },
        remoteBarrierRemoved: (state, action) => {
            state.barriers = state.barriers.filter((b) => b._id !== action.payload)
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createBarrier.pending, (state) => {
                state.isLoading = true
                state.isError = false  // ✅ Reset error state
                state.message = ''     // ✅ Clear message
            })
            .addCase(createBarrier.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false  // ✅ FIXED: Reset error state
                state.message = ''     // ✅ Clear any error message
                // multiuser.md Phase 2c: guard against a race with the
                // entity:created socket broadcast for this same barrier —
                // see the matching comment in threatsSlice.js.
                if (!state.barriers.some((b) => b._id === action.payload._id)) {
                    state.barriers.push(action.payload)
                }
            })
            .addCase(createBarrier.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false // ✅ FIXED: Reset success state
                state.message = action.payload
            })
            .addCase(getBarriers.pending, (state) => {
                state.isLoading = true
                state.isError = false  // ✅ Reset error state
                state.message = ''     // ✅ Clear message
            })
            .addCase(getBarriers.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false  // ✅ FIXED: Reset error state
                state.message = ''     // ✅ Clear any error message
                state.barriers = action.payload
            })
            .addCase(getBarriers.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false // ✅ FIXED: Reset success state
                state.message = action.payload
                state.barriers = []     // ✅ Clear data on error
            })
            .addCase(deleteBarrier.pending, (state) => {
                state.isLoading = true
                state.isError = false  // ✅ Reset error state
                state.message = ''     // ✅ Clear message
            })
            .addCase(deleteBarrier.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.message = ''
                if (action.payload.deleted) {
                    // Full delete or last placement removed
                    state.barriers = state.barriers.filter((item) => item._id !== action.payload._id)
                } else {
                    // Placement removed but barrier still exists — update in place
                    const idx = state.barriers.findIndex((b) => b._id === action.payload._id)
                    if (idx !== -1) state.barriers[idx] = action.payload
                }
            })
            .addCase(deleteBarrier.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false // ✅ FIXED: Reset success state
                state.message = action.payload
            })
            .addCase(updateBarrier.pending, (state) => {
                state.isLoading = true
                state.isError = false  // ✅ Reset error state
                state.message = ''     // ✅ Clear message
            })
            .addCase(updateBarrier.fulfilled, (state, action) => {
                const updatedData = action.payload;
                const existingBarrier = state.barriers.find(barrier => barrier._id === updatedData._id);

                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.message = ''

                if (existingBarrier) {
                    Object.keys(updatedData).forEach(key => {
                        if (key !== '_id') { // Don't overwrite the ID
                            existingBarrier[key] = updatedData[key];
                        }
                    });
                }
            })
            .addCase(updateBarrier.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false
                state.message = action.payload
            })
            .addCase(addPlacement.pending, (state) => {
                state.isLoading = true
                state.isError = false
                state.message = ''
            })
            .addCase(addPlacement.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.message = ''
                const idx = state.barriers.findIndex((b) => b._id === action.payload._id)
                if (idx !== -1) state.barriers[idx] = action.payload
            })
            .addCase(addPlacement.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false
                state.message = action.payload
            })
            .addCase(movePlacement.pending, (state) => {
                state.isLoading = true
                state.isError = false
                state.message = ''
            })
            .addCase(movePlacement.fulfilled, (state, action) => {
                state.isLoading = false
                state.isSuccess = true
                state.isError = false
                state.message = ''
                const idx = state.barriers.findIndex((b) => b._id === action.payload._id)
                if (idx !== -1) state.barriers[idx] = action.payload
            })
            .addCase(movePlacement.rejected, (state, action) => {
                state.isLoading = false
                state.isError = true
                state.isSuccess = false
                state.message = action.payload
            })
    }
})

export const { reset, remoteBarrierUpserted, remoteBarrierRemoved } = barrierSlice.actions
export default barrierSlice.reducer

// --- Selectors ---

/** Returns all barriers that have a placement on the given flowline, sorted by position. */
export const selectBarriersForFlowline = (barriers, type, refId) => {
    if (!refId) return []
    return barriers
        .filter((b) => {
            if (type === 'threat') {
                return (
                    b.threatPlacements?.some((p) => p.threat?.toString() === refId) ||
                    b.threat?.toString() === refId
                )
            }
            return (
                b.consequencePlacements?.some((p) => p.consequence?.toString() === refId) ||
                b.consequence?.toString() === refId
            )
        })
        .map((b) => {
            // Inject position from the matching placement
            if (type === 'threat') {
                const pl = b.threatPlacements?.find((p) => p.threat?.toString() === refId)
                return pl ? { ...b, position: pl.position } : b
            }
            const pl = b.consequencePlacements?.find((p) => p.consequence?.toString() === refId)
            return pl ? { ...b, position: pl.position } : b
        })
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
}

/** Returns the total number of flowline placements for a barrier. */
export const selectPlacementCount = (barrier) => {
    if (!barrier) return 0
    const threatCount = barrier.threatPlacements?.length ?? 0
    const consequenceCount = barrier.consequencePlacements?.length ?? 0
    return Math.max(threatCount + consequenceCount, barrier.threat ? 1 : 0)
}
